import { IS_FR, tr } from '@/lib/i18n';
import { useEffect, useState } from 'react';
import { Mail, MessageCircle, Lock } from 'lucide-react';
import { SUPPORT } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';

export default function Blocked() {
  const [email, setEmail] = useState<string | null>(null);
  const [reason, setReason] = useState<string | null>(null);

  useEffect(() => {
    // Best-effort: if the user is still signed in, fetch their email + reason to
    // personalize the page. On failure we just render the generic block message.
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.id) {
          setEmail(session.user.email ?? null);
          const { data } = await supabase
            .from('profiles')
            .select('blocked_reason')
            .eq('id', session.user.id)
            .single();
          if (data?.blocked_reason) setReason(data.blocked_reason);
          // Sign the user out so they land on /auth next time they visit.
          await supabase.auth.signOut();
        }
      } catch {
        // ignore — page still renders
      }
    })();
  }, []);

  const isGiftRevoked = reason === 'gift_revoked_owner_refund';

  const reasonLabel = (() => {
    if (!reason) return null;
    if (isGiftRevoked) return tr("Shared access ended");
    if (reason.startsWith('refund_') || reason === 'retro_refund_webhook_gap') return tr("Refunded purchase");
    if (reason.startsWith('chargeback_') || reason === 'retro_chargeback_webhook_gap') return tr("Chargeback");
    return tr("Policy violation");
  })();

  const supportSubject = encodeURIComponent(
    isGiftRevoked
      ? tr("Shared access ended — options to continue")
      : tr("Account access — review request")
  );
  const supportBody = encodeURIComponent(
    isGiftRevoked
      ? tr("Hi,\n\nMy account ({email}) was blocked because the person who shared access with me had their purchase refunded. I'd like to know my options to continue using the program.\n\nThanks.", { email: email ?? tr('unknown email') })
      : tr('Hi,\n\nMy account ({email}) has been blocked and I would like to request a review.\n\nOrder / reason context:\n\nThanks.', { email: email ?? tr('unknown email') })
  );
  const mailtoUrl = `mailto:${SUPPORT.EMAIL}?subject=${supportSubject}&body=${supportBody}`;

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-background p-6"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="max-w-md w-full">
        <div className="bg-card border border-border/60 rounded-3xl p-8 shadow-sm">
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-red-500/10 mb-6 mx-auto">
            <Lock className="w-7 h-7 text-red-500" />
          </div>

          <h1 className="text-2xl font-semibold text-foreground text-center mb-3">
            {isGiftRevoked ? tr("Shared access ended") : tr("Account access blocked")}
          </h1>

          <p className="text-sm text-muted-foreground text-center leading-relaxed mb-6">
            {isGiftRevoked ? (
              <>{tr("The person who shared access with you had their purchase refunded, so the shared access ended. If you'd like your own access to continue using the program, reach out and we'll help you get set up.")}</>
            ) : (
              <>{tr("Your account is currently not available. This usually happens after a refund or a chargeback on the original purchase. If you believe this is a mistake, our team is here to help.")}</>
            )}
          </p>

          {(email || reasonLabel) && (
            <div className="rounded-xl bg-muted/40 border border-border/40 p-4 mb-6 text-xs">
              {email && (
                <div className="flex justify-between gap-3 py-0.5">
                  <span className="text-muted-foreground/70 uppercase tracking-wide text-[10px]">{tr("Account")}</span>
                  <span className="text-foreground font-mono truncate max-w-[60%]">{email}</span>
                </div>
              )}
              {reasonLabel && (
                <div className="flex justify-between gap-3 py-0.5">
                  <span className="text-muted-foreground/70 uppercase tracking-wide text-[10px]">{tr("Reason")}</span>
                  <span className="text-foreground">{reasonLabel}</span>
                </div>
              )}
            </div>
          )}

          <div className="space-y-2.5">
            <Button
              asChild
              className="w-full h-11 rounded-xl bg-foreground text-background hover:bg-foreground/90"
            >
              <a href={mailtoUrl}>
                <Mail className="w-4 h-4 mr-2" />{tr("Email support")}</a>
            </Button>

            {!IS_FR && <Button
              asChild
              variant="outline"
              className="w-full h-11 rounded-xl border-border/60"
            >
              <a href={SUPPORT.getWhatsAppUrl()} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4 mr-2" />{tr("Message us on WhatsApp")}</a>
            </Button>}
          </div>

          <p className="text-[11px] text-muted-foreground/60 text-center mt-6">{!IS_FR && <>{tr("Replies usually within 24 hours.")}<br /></>}
            {SUPPORT.EMAIL}
          </p>
        </div>
      </div>
    </div>
  );
}
